<script>
    // Site-wide frictionless upload (roadmap Phase 3 capture-friction work):
    // paste an image anywhere, or drag files onto any page, and it uploads —
    // no form, no fields. The pipeline (OCR/caption/auto-tag) fills in the
    // metadata afterwards; MD5 dedup makes repeats resolve to the same asset.
    import axios from "axios";
    import {page, router} from "@inertiajs/svelte";
    import {userUrl} from "~/components/utils";
    import {Loader2, ImagePlus} from "@lucide/svelte";

    // Pages that run their own paste/drop upload flows — stay out of their way.
    const OWN_UPLOADER = ["media_assets/new", "posts/new", "posts/edit"];

    $: enabled = !!$page.props.current_user && !OWN_UPLOADER.includes($page.component);

    let dragDepth = 0;
    let dragging = false;
    let uploading = false;
    let done = 0;
    let total = 0;
    let failed = 0;

    const mediaFiles = (list) =>
        [...(list || [])].filter(f => f.type.startsWith("image/") || f.type.startsWith("video/"));

    // Paste into the search bar (or any editable target) is not an upload.
    function editableTarget(e) {
        const el = e.target;
        return el && (el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName));
    }

    async function upload(files) {
        if (!files.length || uploading) return;
        uploading = true;
        total = files.length;
        done = 0;
        failed = 0;
        let firstId = null;
        for (const file of files) {
            try {
                const resp = await axios.postForm("/m", {file}, {headers: {"X-Inertia": true}});
                firstId ??= resp.data.media_asset_id;
            } catch (e) {
                failed += 1;
                console.error("upload failed", e);
            }
            done += 1;
        }
        uploading = false;
        if (failed) return; // keep the toast up with the error note
        if (total === 1 && firstId) {
            router.get(`/m/${firstId}`);
        } else if (firstId) {
            router.get(userUrl($page.props.current_user));
        }
    }

    function onpaste(e) {
        if (!enabled || editableTarget(e)) return;
        const files = mediaFiles(e.clipboardData?.files);
        if (!files.length) return;
        e.preventDefault();
        upload(files);
    }

    const hasFiles = (e) => [...(e.dataTransfer?.types || [])].includes("Files");

    // dragenter/leave fire for every child element — depth counter keeps the
    // overlay from flickering.
    function ondragenter(e) {
        if (!enabled || !hasFiles(e)) return;
        e.preventDefault();
        dragDepth += 1;
        dragging = true;
    }
    function ondragover(e) {
        if (enabled && hasFiles(e)) e.preventDefault();
    }
    function ondragleave(e) {
        if (!enabled || !hasFiles(e)) return;
        dragDepth = Math.max(0, dragDepth - 1);
        if (dragDepth === 0) dragging = false;
    }
    function ondrop(e) {
        if (!enabled || !hasFiles(e)) return;
        e.preventDefault();
        dragDepth = 0;
        dragging = false;
        upload(mediaFiles(e.dataTransfer.files));
    }
</script>

<svelte:window {onpaste} {ondragenter} {ondragover} {ondragleave} {ondrop} />

{#if dragging}
    <div class="fixed inset-0 z-[100] pointer-events-none
                bg-background/80 backdrop-blur-sm
                flex flex-col items-center justify-center gap-3">
        <ImagePlus size={40} class="text-primary" />
        <p class="text-lg font-medium text-foreground">放開上傳 · Drop to upload</p>
    </div>
{/if}

{#if uploading || failed}
    <div class="fixed bottom-4 right-4 z-[100] flex items-center gap-2 rounded-lg border border-border
                bg-card px-4 py-2.5 text-sm shadow-lg">
        {#if uploading}
            <Loader2 size={16} class="animate-spin text-primary" />
            <span>Uploading {done + (done < total ? 1 : 0)}/{total}…</span>
        {:else}
            <span class="text-destructive">{failed}/{total} upload{total === 1 ? '' : 's'} failed</span>
            <button class="text-muted-foreground hover:text-foreground ml-1" aria-label="Dismiss"
                    onclick={() => failed = 0}>✕</button>
        {/if}
    </div>
{/if}
